import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { Select, Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { FolderResource, FileResource, Document } from 'app/shared/modules/folders/folders.types';
import { FoldersTabViewFileComponent } from 'app/shared/modules/folders/view-file/view-file.component';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';
import { TsSnackbarService } from 'tsui';
import { FolderResourceState } from '@shared/modules/folders/state/state';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { FeatureFlag } from '@core/enums';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';

@Component({
  selector: 'project-panel-files',
  templateUrl: './files.component.html',
  styleUrls: ['./files.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsFilesComponent
  extends PanelsInterfaceComponentBase
  implements PanelsInterfaceComponent, OnInit, OnDestroy
{
  private _unsubscribeAll: Subject<void>;

  files: FileResource[];
  displayColumns: string[];
  folderId: number;
  isLoading: boolean;

  columnDefs: ColumnDef[] = [];
  newTable: boolean;
  actions: TableActions[] = [];

  @Select(FolderResourceState.getFolder) folder$: Observable<FolderResource>;

  constructor(
    private readonly _foldersTabService: FoldersTabService,
    private readonly _matDialog: MatDialog,
    protected readonly cdRef: ChangeDetectorRef,
    private readonly snackBar: TsSnackbarService,
  ) {
    super(cdRef);
    this._unsubscribeAll = new Subject();
    this.displayColumns = ['name', 'created'];
    this._foldersTabService.accountId = this.store.selectSnapshot(AccountState.getAccount).id;
  }

  ngOnInit() {
    this.checkTable();
    if (this.project) {
      this._foldersTabService.moduleId = this.project.id;
      this._foldersTabService.moduleBaseUrl = 'projects';
    }

    if (this.project.root_folder_resource) {
      this.folderId = this.project.root_folder_resource.id;
      if (this.project.root_folder_resource.file_resources) {
        this.files = this.project.root_folder_resource.file_resources;
      }
    }

    this.titleFieldDef = {
      name: 'files.panel_name',
      label: this.panelDef.title,
      type: 'text',
    };

    this.folder$
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter((folder) => !!folder),
      )
      .subscribe((folder: FolderResource) => {
        this.files = folder.file_resources;
        this.cdRef.markForCheck();
      });

    this.panelColor = this.panelDef?.color;

    if (this.newTable) {
      this.setColDefs();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onFileChanged(event: any) {
    const file: File = event.target.files[0];

    if (!file) return;

    this.isLoading = true;

    const fileResource = {
      folder_resource_id: this.folderId,
      name: file?.name,
    };

    this._foldersTabService
      .createFile(this.folderId, fileResource)
      .pipe(
        takeUntil(this._unsubscribeAll),
        switchMap((fileResource) => this._foldersTabService.uploadDocument(this.folderId, fileResource.id, file)),
      )
      .subscribe({
        next: (newFile) => {
          this.files = [...this.files, newFile];
          this.snackBar.open('Successfully uploaded file', 'success');
        },
        complete: () => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  viewFile(fileResource: FileResource): void {
    const index = this.files.findIndex((item) => item.id === fileResource.id);

    if (index > -1) {
      let file = this.files[index];

      if (!this.readonly) {
        this._matDialog
          .open(FoldersTabViewFileComponent, {
            panelClass: 'mailbox-compose-dialog',
            minWidth: '100vw',
            height: '100vh',
            data: {
              fileResource: file,
            },
          })
          .afterClosed()
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe(() => {
            this.cdRef.markForCheck();
          });
      } else {
        this.downloadDocument(file.documents[0]);
      }
    }
  }

  downloadDocument(document: Document): void {
    window.open(document.original_url, '_blank');
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  setColDefs(): void {
    this.columnDefs = [
      {
        label: 'Name',
        name: 'name',
        type: 'text',
        tsTableOptions: {
          headerName: 'Name',
          field: 'name',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Created',
        name: 'created_at',
        type: 'date',
        tsTableOptions: {
          headerName: 'Created',
          field: 'created_at',
          editable: false,
          flex: 1,
        },
      },
    ];

    this.setTableActions();
  }

  checkTable() {
    this.newTable = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE));
  }

  setTableActions(): void {
    this.actions.push({
      label: 'View File',
      action: (item) => this.viewFile(item),
      icon: 'visibility',
    });
  }
}
